import { cn, extractTags, removeHashtags } from "@moe/priv/utils";
import { VerticalCardView } from "@web/components/VerticalCardView";
import { useAppContext } from "@web/route-services/root/AppContext";
import { BaseData } from "@web/route-services/studio/schema";
import { AnimatePresence, motion } from "motion/react";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

interface Props {
  className?: string;
}

export function Preview({ className }: Props) {
  const form = useFormContext<BaseData>();
  const { profile } = useAppContext();

  // Only subscribe to the specific fields we need instead of the entire form.
  // Watching the whole form causes React to re-render this component whenever *any* field
  // (including `avatar` – a potentially large File object) changes, which leads to
  // noticeable lag and duplicate image fetches after the dependency upgrades.
  const avatarDisplay = useWatch({ control: form.control, name: "avatarDisplay" });
  const title = useWatch({ control: form.control, name: "title" });
  const tagline = useWatch({ control: form.control, name: "tagline" });
  const tags = useMemo(() => extractTags(tagline || ""), [tagline]);

  /**
   * Only show preview if form is empty
   */
  const isEmpty = useMemo(() => {
    return !title && !tagline && !avatarDisplay && tags?.length === 0;
  }, [avatarDisplay, tagline, tags?.length, title]);

  return (
    <AnimatePresence>
      {!isEmpty && (
        <motion.div
          className={cn("", className)}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <VerticalCardView
            title={title}
            tags={tags}
            tagline={removeHashtags(tagline)}
            avatar={avatarDisplay}
            createdBy={profile?.username || "You"}
            disableCreatorLink={true}
            likeCount={452}
            isLiked={false}
            messageCount={12418}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
